import { Show, createMemo, createSignal, type Component } from 'solid-js';
import { Text } from '~ui/text';
import { formatCurrency, formatDay } from '~/lib/format';
import { candles } from '~data';

// A fund's closes, from the first bar on file to the last.
//
// Drawn in a unitless viewBox and stretched to whatever width it is given, so
// `preserveAspectRatio='none'` is deliberate — and every stroke carries
// `vector-effect='non-scaling-stroke'` so that stretch cannot thicken the line
// horizontally and thin it vertically. Anything that would be distorted by it
// (the hover dot, which must stay round) is an HTML element positioned over the
// top in percentages rather than an SVG shape inside it.
//
// Only `close` is used. Open, high and low are nullable in the feed and absent
// for much of the history, so a calculation reaching for them would work on some
// symbols and quietly not on others.
const VB_W = 100;
const VB_H = 40;
const PAD = 2;
// Horizontal inset, in the same viewBox units. Without it the last sample sits
// exactly on x=100 and the 2px stroke is half-clipped by the panel edge.
const PAD_X = 3;

export const CloseChart: Component<{ symbol: string; height?: string }> = (props) => {
    const series = createMemo(() => candles(props.symbol));

    const bounds = createMemo(() => {
        const closes = series().map((c) => c.close);
        if (closes.length === 0) return null;
        const min = Math.min(...closes);
        const max = Math.max(...closes);
        // A flat series would divide by zero; give it a band so the line lands
        // in the middle rather than at an edge.
        return { min, max, span: max - min || Math.abs(max) || 1 };
    });

    const x = (i: number): number => (series().length <= 1 ? VB_W / 2 : PAD_X + (i / (series().length - 1)) * (VB_W - PAD_X * 2));
    const y = (v: number): number => {
        const b = bounds()!;
        return VB_H - PAD - ((v - b.min) / b.span) * (VB_H - PAD * 2);
    };

    const path = createMemo(() => {
        if (!bounds()) return '';
        return series()
            .map((c, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(3)} ${y(c.close).toFixed(3)}`)
            .join(' ');
    });

    // Nearest sample to the pointer. The hit area is the whole plot, not the 2px
    // line — landing on a stroke is not a thing anyone can do.
    const [hover, setHover] = createSignal<number | null>(null);
    const point = () => (hover() == null ? null : series()[hover()!]);

    const track = (e: MouseEvent & { currentTarget: HTMLDivElement }) => {
        const rect = e.currentTarget.getBoundingClientRect();
        if (rect.width <= 0 || series().length === 0) return;
        // Invert x(), rather than mapping the pointer across the whole box. The
        // plot is inset by PAD_X at each end, so a pointer fraction taken over
        // the full width lands the crosshair PAD_X to the right of the cursor at
        // the left edge and PAD_X to the left of it at the right — correct only
        // in the exact middle, which is where it is easiest not to notice.
        const across = ((e.clientX - rect.left) / rect.width) * VB_W;
        const fraction = Math.min(1, Math.max(0, (across - PAD_X) / (VB_W - PAD_X * 2)));
        setHover(Math.round(fraction * (series().length - 1)));
    };

    return (
        <Show when={bounds()} fallback={<Text tone='muted' class='block px-2 py-4 text-center'>No price history</Text>}>
            <div class={`relative w-full ${props.height ?? 'h-28'}`} onMouseMove={track} onMouseLeave={() => setHover(null)}>
                <svg class='h-full w-full' viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio='none' fill='none'>
                    <path d={path()} class='stroke-foreground' stroke-width='2' stroke-linejoin='round' stroke-linecap='round' vector-effect='non-scaling-stroke' />
                    <Show when={hover() != null}>
                        <line x1={x(hover()!)} y1='0' x2={x(hover()!)} y2={VB_H} class='stroke-muted-foreground' stroke-width='1' vector-effect='non-scaling-stroke' />
                    </Show>
                </svg>

                {/* Positioned in percentages over the plot rather than drawn in
                    it, so the non-uniform scale cannot squash it into an oval. */}
                <Show when={point()}>
                    <div
                        class='pointer-events-none absolute size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-foreground'
                        style={{ left: `${(x(hover()!) / VB_W) * 100}%`, top: `${(y(point()!.close) / VB_H) * 100}%` }}
                    />
                </Show>
            </div>

            {/* The tooltip enhances; it never gates. Every figure it shows is also
                in this row, which is the table view for the chart. */}
            <div class='flex h-row items-center justify-between gap-2 border-t px-2 whitespace-nowrap'>
                <Show
                    when={point()}
                    fallback={
                        <>
                            <Text tone='muted'>
                                {formatDay(series()[0].date)} — {formatDay(series()[series().length - 1].date)}
                            </Text>
                            <Text tone='muted'>
                                low {formatCurrency(bounds()!.min)} · high {formatCurrency(bounds()!.max)}
                            </Text>
                        </>
                    }>
                    <Text tone='muted'>{formatDay(point()!.date)}</Text>
                    <Text weight='medium'>{formatCurrency(point()!.close)}</Text>
                </Show>
            </div>
        </Show>
    );
};

export default CloseChart;
